import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ExternalLink, History, Loader2, LogOut, ShieldCheck, Zap, ChevronDown } from 'lucide-react';
import { formatDateTime as formatDate } from '../utils/formatDateTime';

function friendlyRecommendationKey(key) {
  if (!key) return key;
  // e.g. mobile_top_fix → Top Fix (Mobile)
  //      desktop_next_fix_0 → Fix #1 (Desktop)
  const topMatch = key.match(/^(mobile|desktop)_top_fix$/);
  if (topMatch) return `Top Fix (${topMatch[1].charAt(0).toUpperCase() + topMatch[1].slice(1)})`;
  const nextMatch = key.match(/^(mobile|desktop)_next_fix_(\d+)$/);
  if (nextMatch) {
    const strategy = nextMatch[1].charAt(0).toUpperCase() + nextMatch[1].slice(1);
    return `Fix #${Number(nextMatch[2]) + 1} (${strategy})`;
  }
  return key;
}

function scoreColor(score) {
  if (score === null || score === undefined) return 'text-slate-400';
  if (score >= 90) return 'text-emerald-400';
  if (score >= 50) return 'text-amber-400';
  return 'text-red-400';
}

const JOB_STATUS_STYLE = {
  completed: 'bg-emerald-500/20 text-emerald-300',
  skipped: 'bg-amber-500/20 text-amber-300',
  failed: 'bg-red-500/20 text-red-300',
  running: 'bg-blue-500/20 text-blue-300',
};

function UsagePill({ label, used, limit }) {
  const pct = limit > 0 ? Math.min(used / limit, 1) : 0;
  const barColor = pct >= 1 ? 'bg-red-400' : pct >= 0.75 ? 'bg-amber-400' : 'bg-blue-400';
  return (
    <div className="flex flex-col gap-1 min-w-[120px]">
      <div className="flex items-center justify-between text-xs text-slate-400">
        <span>{label}</span>
        <span className="font-semibold text-slate-200">{used}<span className="font-normal text-slate-500">/{limit}</span></span>
      </div>
      <div className="h-1 rounded-full bg-slate-700 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct * 100}%` }} />
      </div>
    </div>
  );
}

function CollapsibleSection({ title, subtitle, badge, isExpanded, onToggle, children }) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2 text-left hover:bg-slate-800/50 transition-colors group"
      >
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-xs font-semibold text-slate-300">{title}</p>
          {badge}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {subtitle && <p className="text-xs text-slate-500 hidden sm:block">{subtitle}</p>}
          <ChevronDown
            size={14}
            className={`text-slate-500 transition-transform group-hover:text-slate-400 shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>
      {isExpanded && <div className="mt-2">{children}</div>}
    </div>
  );
}

function ShowMoreControls({ visibleCount, totalCount, defaultCount, onShowMore, onShowLess }) {
  const remaining = totalCount - visibleCount;
  if (visibleCount <= defaultCount && remaining <= 0) return null;
  return (
    <div className="mt-2 flex items-center gap-3">
      {remaining > 0 && (
        <button
          type="button"
          onClick={onShowMore}
          className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
        >
          Show {Math.min(remaining, 5)} more →
        </button>
      )}
      {visibleCount > defaultCount && (
        <button
          type="button"
          onClick={onShowLess}
          className="text-xs text-slate-500 hover:text-slate-400 transition-colors"
        >
          Show less
        </button>
      )}
    </div>
  );
}

function RecentAnalysesList({ analyses, total, loading, loadingMore, allLoaded, onLoad, onLoadMore, loadingItem }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const sentinelRef = useRef(null);

  const handleObserver = useCallback((entries) => {
    if (entries[0].isIntersecting && !loadingMore && !allLoaded) {
      onLoadMore && onLoadMore();
    }
  }, [loadingMore, allLoaded, onLoadMore]);

  useEffect(() => {
    if (!isExpanded) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isExpanded, handleObserver]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-slate-500">
        <Loader2 size={12} className="animate-spin" />
        Loading your analyses…
      </div>
    );
  }

  if (!analyses.length) {
    return (
      <p className="text-xs text-slate-500 italic">No saved analyses yet. Run your first analysis above to save it to your account.</p>
    );
  }

  const displayTotal = total ?? analyses.length;

  return (
    <CollapsibleSection
      title={`Recent Analyses (${displayTotal})`}
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded((v) => !v)}
    >
      <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {analyses.map((a) => {
          const mobileRec = (a.recommendations || []).find((r) => r.strategy === 'mobile' && r.recommendation_key?.endsWith('_top_fix'));
          const desktopRec = (a.recommendations || []).find((r) => r.strategy === 'desktop' && r.recommendation_key?.endsWith('_top_fix'));
          const displayRec = mobileRec || desktopRec;
          const isThisLoading = loadingItem === a.analysis_id;
          return (
            <li key={a.analysis_id}>
              <button
                type="button"
                onClick={() => !loadingItem && onLoad && onLoad(a)}
                disabled={!!loadingItem}
                className={`w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${
                  isThisLoading
                    ? 'border-blue-500/40 bg-slate-800/80 cursor-wait'
                    : loadingItem
                    ? 'border-white/5 bg-slate-900/50 opacity-50 cursor-not-allowed'
                    : 'border-white/5 bg-slate-900/50 hover:border-blue-500/30 hover:bg-slate-800/60'
                }`}
                title="Load this analysis"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-200">{a.url}</p>
                  <p className="text-xs text-slate-500">
                    {isThisLoading ? 'Loading…' : formatDate(a.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {displayRec && !isThisLoading && (
                    <div className="text-right">
                      <span className={`text-xs font-bold ${scoreColor(displayRec.score)}`}>
                        {displayRec.score ?? '—'}
                      </span>
                      <p className="text-xs text-slate-600">{displayRec.strategy || ''}</p>
                    </div>
                  )}
                  {isThisLoading
                    ? <Loader2 size={14} className="animate-spin text-blue-400" />
                    : <span className="text-xs text-blue-400 font-medium">View →</span>
                  }
                </div>
              </button>
            </li>
          );
        })}
        {/* Sentinel triggers next page load when scrolled into view */}
        {!allLoaded && (
          <li ref={sentinelRef} className="flex items-center justify-center py-2">
            {loadingMore
              ? <><Loader2 size={12} className="animate-spin text-slate-500 mr-1" /><span className="text-xs text-slate-500">Loading more…</span></>
              : <span className="text-xs text-slate-600">Scroll for more</span>
            }
          </li>
        )}
      </ul>
    </CollapsibleSection>
  );
}

function RecentJobsList({ jobs, loading }) {
  const [jobsExpanded, setJobsExpanded] = useState(jobs.length > 0);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    if (jobs.length > 0) setJobsExpanded(true);
  }, [jobs.length]);

  return (
    <div className="mt-4">
      <CollapsibleSection
        title="Draft PR Jobs"
        subtitle="History of automated fix jobs and their draft PR links."
        badge={
          loading === 'idle' ? <span className="text-xs text-slate-600 italic">queued…</span>
          : loading === 'loading' ? <Loader2 size={11} className="animate-spin text-slate-500" />
          : <span className="text-xs text-slate-500">({jobs.length})</span>
        }
        isExpanded={jobsExpanded}
        onToggle={() => setJobsExpanded((v) => !v)}
      >
        {loading !== 'done' ? (
          <div className="flex items-center gap-2 py-2 text-xs text-slate-500">
            <Loader2 size={12} className="animate-spin" />
            {loading === 'idle' ? 'Queued, loading soon…' : 'Loading jobs…'}
          </div>
        ) : jobs.length === 0 ? (
          <p className="text-xs text-slate-500 italic px-1">No fix jobs yet.</p>
        ) : (
          <>
            <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {jobs.slice(0, visibleCount).map((j) => (
                <li key={j.job_id} className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-slate-900/50 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium text-slate-300">{friendlyRecommendationKey(j.recommendation_key)}</p>
                    <p className="text-xs text-slate-500">{j.repository_full_name} · {formatDate(j.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${JOB_STATUS_STYLE[j.status] || 'bg-slate-700 text-slate-300'}`}>
                      {j.status}
                    </span>
                    {j.pr_url && (
                      <a
                        href={j.pr_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                        title="View draft PR"
                      >
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <ShowMoreControls
              visibleCount={visibleCount}
              totalCount={jobs.length}
              defaultCount={3}
              onShowMore={() => setVisibleCount((c) => c + 5)}
              onShowLess={() => setVisibleCount(3)}
            />
          </>
        )}
      </CollapsibleSection>
    </div>
  );
}

export function AuthOnboardingCard({
  supabaseConfigured,
  authReady,
  authLoading,
  accountContext,
  onboardingForm,
  onboardingSubmitting,
  onWorkspaceNameChange,
  onPrimaryEmailChange,
  onSignIn,
  onSignOut,
  onSubmitOnboarding,
  userHistory,
  userHistoryLoading,
  userHistoryLoadingMore,
  userJobsLoading,
  loadingHistoryAnalysis,
  userUsage,
  onLoadAnalysis,
  onLoadMoreAnalyses,
}) {
  const [loadingAnalysisId, setLoadingAnalysisId] = useState(null);

  useEffect(() => {
    if (!loadingHistoryAnalysis) setLoadingAnalysisId(null);
  }, [loadingHistoryAnalysis]);

  const handleLoadAnalysisWithFeedback = (analysis) => {
    setLoadingAnalysisId(analysis.analysis_id);
    onLoadAnalysis && onLoadAnalysis(analysis);
  };

  const signedIn = Boolean(accountContext?.accessToken);
  const needsOnboarding = Boolean(accountContext?.needsOnboarding);
  const tenant = accountContext?.tenant || null;
  const user = accountContext?.user || null;

  const analyses = userHistory?.analyses || [];
  const jobs = userHistory?.jobs || [];
  const analysesUsed = userUsage?.analyses_used ?? null;
  const analysesRemaining = userUsage?.analyses_remaining ?? null;
  const analysesLimit = analysesUsed !== null && analysesRemaining !== null ? analysesUsed + analysesRemaining : null;
  const prsUsed = userUsage?.prs_used ?? null;
  const prsRemaining = userUsage?.prs_remaining ?? null;
  const prsLimit = prsUsed !== null && prsRemaining !== null ? prsUsed + prsRemaining : null;
  const showUsage = analysesUsed !== null && analysesLimit !== null;
  const billingResetDate = (() => {
    if (!userUsage?.billing_period_start) return null;
    const start = new Date(userUsage.billing_period_start + 'T00:00:00');
    const next = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    return next.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  })();

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-slate-950/60 p-5 text-left shadow-2xl backdrop-blur">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-200">
            <ShieldCheck size={14} />
            Save Your Work
          </div>
          {signedIn && user ? (
            <h2 className="text-xl font-bold font-chivo text-white">
              Welcome back{user.display_name ? `, ${user.display_name.split(' ')[0]}` : ''}
            </h2>
          ) : (
            <h2 className="text-xl font-bold font-chivo text-white">
              Sign in to save your analysis history
            </h2>
          )}
          {!signedIn && (
            <p className="max-w-2xl text-sm text-slate-300">
              Your analyses and fix jobs are saved to your account so you can return to them anytime, track progress across sessions, and unlock higher usage limits.
            </p>
          )}
        </div>

        {signedIn && (
          <button
            type="button"
            onClick={onSignOut}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-800"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        )}
      </div>

      {!supabaseConfigured && (
        <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          Sign-in is not available in this environment yet.
        </div>
      )}

      {supabaseConfigured && !authReady && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3 text-sm text-slate-200">
          <Loader2 size={16} className="animate-spin" />
          Restoring your session...
        </div>
      )}

      {supabaseConfigured && authReady && !signedIn && (
        <div className="mt-4 flex flex-col gap-4 rounded-xl border border-slate-700 bg-slate-900/70 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-white">No active session</p>
            <p className="mt-1 text-sm text-slate-300">
              Sign in with Google to save analyses, track fix jobs, and keep your history across sessions.
            </p>
          </div>
          <button
            type="button"
            onClick={onSignIn}
            disabled={authLoading}
            className="rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {authLoading ? 'Redirecting…' : 'Continue with Google'}
          </button>
        </div>
      )}

      {supabaseConfigured && authReady && signedIn && user && needsOnboarding && (
        <form className="mt-4 rounded-xl border border-blue-400/20 bg-blue-500/10 p-4" onSubmit={onSubmitOnboarding}>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-blue-100">Set up your workspace</p>
            <p className="text-sm text-blue-50/90">
              One last step — give your workspace a name so we can save your analyses and fix history to your account.
            </p>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-blue-100">
                Workspace Name
              </span>
              <input
                type="text"
                value={onboardingForm.workspaceName}
                onChange={(event) => onWorkspaceNameChange(event.target.value)}
                placeholder="My Agency"
                className="w-full rounded-xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white placeholder:text-slate-500"
                disabled={onboardingSubmitting}
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-blue-100">
                Primary Email
              </span>
              <input
                type="email"
                value={onboardingForm.primaryEmail}
                onChange={(event) => onPrimaryEmailChange(event.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white placeholder:text-slate-500"
                disabled={onboardingSubmitting}
              />
            </label>
          </div>

          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <p className="text-xs text-blue-100/80">
              Your analyses and fix jobs will be linked to this workspace going forward.
            </p>
            <button
              type="submit"
              disabled={onboardingSubmitting}
              className="rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {onboardingSubmitting ? 'Setting up…' : 'Set Up Workspace'}
            </button>
          </div>
        </form>
      )}

      {supabaseConfigured && authReady && signedIn && user && !needsOnboarding && tenant && (
        <div className="mt-4 space-y-4">
          <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm font-semibold text-emerald-100">
                  {user.display_name || user.email}
                </p>
                <p className="mt-0.5 text-sm text-emerald-50/80">
                  {tenant.name}
                </p>
              </div>
              {showUsage && (
                <div className="flex flex-col gap-1.5">
                  <div className="flex flex-wrap gap-4">
                    <UsagePill label="Analyses" used={analysesUsed} limit={analysesLimit} />
                    {prsUsed !== null && prsLimit !== null && (
                      <UsagePill label="Draft PRs" used={prsUsed} limit={prsLimit} />
                    )}
                  </div>
                  {billingResetDate && (
                    <p className="text-xs text-slate-500">Resets {billingResetDate}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center gap-2">
              <History size={14} className="text-slate-400" />
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Previous Analyses</p>
            </div>
            <RecentAnalysesList
              analyses={analyses}
              total={userHistory?.total ?? null}
              loading={userHistoryLoading}
              loadingMore={userHistoryLoadingMore}
              allLoaded={userHistory?.allLoaded ?? true}
              onLoad={handleLoadAnalysisWithFeedback}
              onLoadMore={onLoadMoreAnalyses}
              loadingItem={loadingAnalysisId}
            />
            <RecentJobsList jobs={jobs} loading={userJobsLoading} />
          </div>

          {analyses.length > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-white/5 bg-slate-900/40 px-3 py-2">
              <Zap size={12} className="text-blue-400 shrink-0" />
              <p className="text-xs text-slate-400">
                Your analyses are saved. Run a new one above or re-analyse any URL to update your history.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
